/*
 * Detalle de orden de traspaso.
 *
 * Información del traspaso, muestra el estado en que se encuentrá y las opciones para los
 * distintos roles que pueden manipularlo. Las listas internas en el detalle son productos
 * y circuitos. La edición del trespaso también se controla en este archivo.
 *
 * Created on Tue Mar 21 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <!-- #region::Transfer edition form -->
    <validation-observer ref="transferForm">

      <!-- #region::Skeleton zone -->
      <template v-if="loadingTransfer">
        <TransferSaveFormSkeleton />
      </template>
      <!-- #endregion::Skeleton zone -->

      <template v-else>
        <div class="mb-2">

          <!-- #region::Transfer details options -->
          <TransferDetailOptions
            class="mb-1"
            :callback="onUpdateTransfer"
            :can-manage-transfer="canManageTransfers"
            @reload-transfer="loadTransferData"
          />
          <!-- #endregion::Transfer details options -->

          <TransferDetailInformation @reload-transfer="loadTransferData" />

          <b-col md="12">
            <b-tabs>

              <!-- #region::Transfer products list -->
              <b-tab title="Productos">
                <TransferProducts
                  :show-edit-button="editionModeState && transferOrderStatus !== 'Aprobada'"
                  :show-delete-button="editionModeState"
                  :show-add-product-button="editionModeState && transferOrderStatus !== 'Aprobada'"
                  :show-pieces-list-actions-buttons="editionModeState"
                />
              </b-tab>
              <!-- #endregion::Transfer products list -->

              <!-- #region::Transfer circuits list -->
              <b-tab title="Circuitos">
                <TransferCircuits
                  :show-add-circuit-button="editionModeState && transferOrderStatus !== 'Aprobada'"
                  :show-delete-button="editionModeState"
                />
              </b-tab>
            <!-- #endregion::Transfer circuits list -->

            </b-tabs>
          </b-col>

          <TransferComments
            v-if="canShowComments"
            class="mt-2"
            :username="rejectComment.publisher"
            :comment="rejectComment.comment"
            :date="rejectComment.date"
          />

        </div>
      </template>

    </validation-observer>
    <!-- #endregion::Transfer edition form -->

  </div>
</template>

<script>
// #region Imports
import { mapActions, mapGetters } from 'vuex'

import { ValidationObserver, localize } from 'vee-validate'

import { BCol, BTabs, BTab } from 'bootstrap-vue'

import TransferComments from '@/modules/production/transfers/components/TransferComments.vue'
import TransferProducts from '@/modules/production/transfers/components/form/TransferProducts.vue'
import TransferCircuits from '@/modules/production/transfers/components/form/TransferCircuits.vue'
import TransferDetailOptions from '@/modules/production/transfers/components/TransferDetailOptions.vue'
import TransferSaveFormSkeleton from '@/modules/production/transfers/components/TransferSaveFormSkeleton.vue'
import TransferDetailInformation from '@/modules/production/transfers/components/TransferDetailInformation.vue'

import getError from '@/helpers/ErrorsHandler'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { adminLevel, ADMIN_USER_LEVELS } from '@/helpers/UserLevel'
// #endregion

export default {
  components: {
    BCol,
    BTab,
    BTabs,
    TransferComments,
    TransferCircuits,
    TransferProducts,
    ValidationObserver,
    TransferDetailOptions,
    TransferSaveFormSkeleton,
    TransferDetailInformation,
  },
  props: {
    /**
     * Identificador de la orden de traspaso
     */
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loadingTransfer: true,
      adminLevel,
      ADMIN_USER_LEVELS,
    }
  },
  computed: {
    ...mapGetters({
      /**
       * Orden de traspaso cargada en el state management
       */
      getTransfer: 'transfers/getTransfer',

      /**
       * Atiende el estado de edición de la orden de traspaso
       */
      getEditionModeState: 'transfers/getEditionModeState',

      /**
       * Información de la orden de traspaso
       */
      getSelectedOrigin: 'transfers/getSelectedOrigin',
      getSelectedDestination: 'transfers/getSelectedDestination',
      getSelectedRequiredDate: 'transfers/getSelectedRequiredDate',

      /**
       * Piezas, productos y circuitos de la orden de traspaso
       */
      getTransferPieces: 'transfers/getTransferPieces',
      getTransferProducts: 'transfers/getTransferProducts',
      getTransferCircuits: 'transfers/getTransferCircuits',

      /**
       * Término de ubicación de búsqueda del store de productos
       */
      getLocationTerm: 'products/getLocationTerm',
    }),
    /**
     * Orden de traspaso cargada en el state management
     */
    transfer: {
      get() { return this.getTransfer.transfer },
    },
    /**
     * Atiende el estado de edición de la orden de traspaso
     */
    editionModeState: {
      get() { return this.getEditionModeState },
      set(value) { this.setEditionModeState(value) },
    },
    transferOrderStatus: {
      get() { return this.transfer.StatusText },
    },
    selectedOrigin: {
      get() { return this.getSelectedOrigin },
    },
    selectedDestination: {
      get() { return this.getSelectedDestination },
    },
    selectedRequiredDate: {
      get() { return this.getSelectedRequiredDate },
    },
    transferPieces: {
      get() { return this.getTransferPieces },
    },
    transferProducts: {
      get() { return this.getTransferProducts },
    },
    transferCircuits: {
      get() { return this.getTransferCircuits },
    },
    rejectComment: {
      get() { return this.getTransfer.rejectComment },
    },
    emptyTransfer: {
      get() {
        return this.transferProducts.length === 0
          && this.transferCircuits.length === 0
      },
    },
    /**
     * Término de ubicación de búsqueda del store de productos
     */
    locationTerm: {
      get() { return this.getLocationTerm },
      set(value) { this.setLocationTerm(value) },
    },
    /**
     * Piezas de los circuitos de la transferencia
     */
    circuitPieces: {
      get() {
        const pieces = []

        this.transferCircuits.forEach(circuit => {
          circuit.products.forEach(product => {
            product.pieces.forEach(piece => {
              pieces.push(piece)
            })
          })
        })

        return pieces
      },
    },
    piecesInTransfer: {
      get() {
        return this.transferPieces.length || this.circuitPieces.length
      },
    },
    canShowComments() {
      if (!this.rejectComment) {
        return false
      }

      return this.transferOrderStatus === 'Rechazada'
        || this.transferOrderStatus === 'Cancelado'
        || this.transferOrderStatus === 'Solicitud'
    },
    /**
     * Indica si el usuario puede gestionar traspasos
     */
    canManageTransfers() {
      return this.adminLevel() === this.ADMIN_USER_LEVELS.ADMINISTRADOR
    },
  },

  /**
  * Hook de creación del componente
  *
  * @summary Carga toda la información de la orden de traspaso
  */
  async created() {
    this.loadingTransfer = true

    // Carga la información de la orden de traspaso
    await this.loadTransferData()
    localize('es')

    this.loadingTransfer = false
  },
  /**
  * Llamado antes de cambiar de componente
  *
  * @summary Se encarga de limpiar toda la información en el state
  * management relacionada con el detalle de la orden de traspaso
  */
  beforeDestroy() {
    this.locationTerm = ''
    this.clearFormData()
  },
  methods: {
    ...mapActions({
      loadTransfer: 'transfers/loadTransfer',
      clearFormData: 'transfers/clearFormData',
      updateTransfer: 'transfers/updateTransfer',
      setLocationTerm: 'products/setLocationTerm',
      setEditionModeState: 'transfers/setEditionModeState',
      setOriginProductRoute: 'products/setOriginProductRoute',
    }),

    /**
    * Carga el registro de la transferencia seleccionada a partir del identificador
    *
    * @summary Carga el registro de traspaso y establece la ruta de origen en el caso
    * de que se seleccine el detalle de un producto, la redirección será al detalle en cuestión
    * @exception ⁡⁢⁣⁢Error de validación en la carga del registro de traspaso
    */
    async loadTransferData() {
      try {
        this.$swal({
          title: 'Cargando...',
          allowOutsideClick: false,
        })
        this.$swal.showLoading()

        const response = await this.loadTransfer(this.id)
        const { transfer } = response.data.data

        // Establece la ruta para cuando se va al detalle del producto
        this.setOriginProductRoute({
          name: this.$route.name,
          params: {
            id: transfer.IdTransfer,
          },
        })

        this.$swal.close()
      } catch (error) {
        this.$swal.close()
        if (error.response.status === 404) {
          this.$router.push({ name: 'error-404' })
        }

        // this.showToast('Error de validación', getError(error), 'danger')
        // this.$router.push({ name: 'transfers-list' })
      } finally {
        this.editionModeState = false
      }
    },

    /**
    * Actualiza el registro de traspaso
    *
    * @summary Actualiza la información del registro de traspaso, así como las piezas
    * de los productos y los circuitos
    * @exception ⁡⁢⁣⁢Error de validación en la actualización del registro de traspaso
    */
    async onUpdateTransfer() {
      const success = await this.$refs.transferForm.validate()

      if (!success) {
        return
      }

      if (this.emptyTransfer || !this.piecesInTransfer) {
        const result = await this.$swal({
          title: '¿Deseas continuar?',
          text: '¡Al no contar con piezas, la orden de traspaso tomará el estatus cancelada!',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Aceptar',
          cancelButtonText: 'Cancelar',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })

        if (!result.isConfirmed) {
          return
        }
      }

      if (success) {
        try {
          this.$swal({
            title: 'Espere por favor',
            allowOutsideClick: false,
          })

          this.$swal.showLoading()

          const formData = new FormData()

          // * Si existen productos en el formalario, serán agregados al formData.
          this.transferProducts.forEach(transferProduct => {
            formData.append('circuits_products[]', this.buildProductData(transferProduct))
          })

          // * Si existen circuitos en el formulario, serán agregados al formData.
          this.transferCircuits.forEach(transferCircuit => {
            transferCircuit.products.forEach(product => {
              formData.append('circuits_products[]', this.buildProductData(product, transferCircuit.IdCircuit))
            })
          })

          formData.append('locationOrigin', this.selectedOrigin.text)
          formData.append('dateRequired', this.selectedRequiredDate)
          formData.append('locationDestiny', this.selectedDestination.text)

          // eslint-disable-next-line no-restricted-syntax
          for (const pair of formData.entries()) {
            console.log(`${pair[0]}, ${pair[1]}`)
          }

          await this.updateTransfer({ id: this.transfer.ID, formData })

          this.showToast(
            'Orden actualizada',
            'Lar orden ha sido actualizada correctamente.',
            'success',
          )
          await this.loadTransferData()
        } catch (error) {
          this.$swal.close()
          this.showToast('Error de validación', getError(error), 'danger')
        }
      } else {
        this.showToast(
          'Error de validación',
          'Se encontraron uno o más errores en la validación del formulario de la orden de traspaso.',
          'danger',
        )
      }
    },

    /**
    * Construye un objeto de tipo producto con sus piezas en formato JSON
    *
    * @param product - Información del producto
    * @param {number, string} idCircuit - Identificador del circuito. Especificado si el producto pertenece a un circuito
    * @return Producto con piezas en format JSON
    */
    buildProductData(product, idCircuit = '') {
      // Create an array to hold the pieces for the product
      const pieces = []

      // Iterate through the pieces in the product and add them to the array
      product.pieces.forEach(piece => {
        pieces.push({ idPiece: piece.IdPiece })
      })

      // Create a JSON string with the product data
      const productData = JSON.stringify({
        idCircuit,
        idProduct: product.IdProduct,
        qty: product.pieces.length,
        pieces,
      })

      return productData
    },

    /**
    * Muestra un mensaje de tipo toast
    *
    * @param {string} title - Título del mensaje
    * @param {string} text - Contenido del mensaje
    * @param {string} variant - Tipo del mensaje (warning, success, danger)
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>
